/**
 * Shared LN2 message type constants and helpers.
 * Used across protocol-specific CDO editors (Modbus, STM32, Numato, etc.).
 *
 * The LN2 FlatBuffer message hierarchy:
 *   FrontEndMessage → ValuedMessage → Message → EncodedMessageContent
 *     EncodedMessageContent → OneHotMessage | BitMaskMessage
 *       BitMaskMessage → StatusMessage | TrafficMessage | ControlMessage | FaultMessage
 *                       | GroupSupervisoryStatusMessage | GroupControlMessage | ControllerInfoMessage
 *       OneHotMessage → UsageMessage | ServiceMessage
 *         UsageMessage → ElevatorUsageMessage | EscalatorUsageMessage
 *         ServiceMessage → ElevatorServiceMessage | ServiceMessage
 *
 * The adapter configuration is device-type-agnostic. The elevator/escalator
 * wrapper is determined at auto-expand time from the bank's device type.
 */
import { EncodedMsgContent } from "../../../interfaces/AdapterCdoModbus.interface";
import {
  LN2_MESSAGE_CATEGORIES,
  parseCompositeKey,
  buildEncodedMsgContent,
  encodedMsgContentCategoryKey,
  DeviceType,
} from "../../../flatbuffers/types/ln2MessageOptions";

// Flat option list for a Select dropdown (device-type-agnostic)
export const LN2_MESSAGE_TYPES = LN2_MESSAGE_CATEGORIES.map((cat) => ({
  label: cat.label,
  value: cat.categoryKey,
}));

/** Build the nested EncodedMsgContent JSON from a composite key + device type. */
export function buildMessageContent(
  compositeKey: string,
  deviceType?: DeviceType,
): EncodedMsgContent {
  const result = buildEncodedMsgContent(compositeKey, deviceType);
  if (result) return result;
  // Fallback for category-only key (no enum value specified)
  return {
    bit_mask_message: { message: { status_message: {} } },
  };
}

/** Parse the nested EncodedMsgContent back into a category key (defaults to
 *  "status" for unrecognized content — see encodedMsgContentCategoryKey). */
export function parseMessageType(content: EncodedMsgContent): string {
  return encodedMsgContentCategoryKey(content) ?? "status";
}

/** Build a default FEMessage (used by Modbus mapping entries). */
export function buildDefaultFEMessage() {
  return {
    msg: {
      msg: {
        content: { bit_mask_message: { message: { status_message: {} } } },
      },
      active: true,
    },
    bank_name: "",
    lift_name: "",
    bank_id: "",
    lift_id: "",
  };
}

/** Re-export for convenience */
export { parseCompositeKey, LN2_MESSAGE_CATEGORIES };
